import React, { useEffect, useMemo, useState } from 'react';
import { AutocompleteInput, required, useNotify } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { API_ENDPOINTS } from 'dd-cms-client/app/dataProvider';
import { ResourceName } from 'dd-cms-client/app/resources';
import { getTenant } from 'dd-cms-client/auth/utils/tenant';
import { CategoryStatus } from 'dd-cms-client/category/CategoryList';
import { useRequest } from 'dd-cms-client/common/hooks/useRequest';
import { showErrorNotification } from 'dd-cms-client/common/utils/error';
import { Header } from 'dd-cms-client/common/utils/request';
import { Component, Props } from './types';

enum CategoryType {
  ADDITIONAL = 'additional',
  MAIN = 'main',
}

const NewsletterCategorySelector: Component = ({
  source,
  isDisabled,
  ...restInputProps
}: Props): React.ReactElement | null => {
  const { getValues, setValue } = useFormContext();
  const notify = useNotify();
  const tenant = getTenant();
  const [availableCategory, setAvailableCategory] = useState<Array<{
    id: number;
    name: string;
  }>>([]);
  const { t } = useTranslation();

  const categoryId = getValues().fields?.categoryId;
  const [selectedCategoryType, setSelectedCategoryType] = useState<CategoryType | null>(null);

  const disabledClassName = classNames(
    {
      'Field--Disabled': isDisabled,
    },
  );

  const text = useMemo(
    () => ({
      newsletterCategory: t('Newsletter category'),
    }),
    [t],
  );

  const {
    makeRequest: getNewsletterCategories,
    error: newsletterError,
    isError: isNewsletterError,
    isSuccess: isNewsletterSuccess,
    data: newsletterData,
  } = useRequest(
    `${API_ENDPOINTS[ResourceName.CATEGORY]}/category?fields_type=${CategoryType.MAIN}`,
    'get',
    {
      [Header.TENANT]: tenant,
    },
  );

  const {
    makeRequest: getAllCategories,
    error,
    isError,
    isSuccess,
    data,
  } = useRequest(
    `${API_ENDPOINTS[ResourceName.CATEGORY]}/category`,
    'get',
    {
      [Header.TENANT]: tenant,
    },
  );

  useEffect(
    () => {
      if (availableCategory.length === 0) {
        getAllCategories();
        getNewsletterCategories();
      }

      return () => setAvailableCategory([]);
    },
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(
    () => {
      if (isNewsletterSuccess && newsletterData?.length) {
        setAvailableCategory(
          newsletterData
            .filter((category: { fields: { active: CategoryStatus } }) => category.fields.active === CategoryStatus.ACTIVE)
            .map((category: { id: number; fields: { name: string } }) => ({
              id: category.id,
              name: category.fields.name,
            })),
        );
      }

      if (isNewsletterError) {
        showErrorNotification(newsletterError, notify);
      }
    },
    [isNewsletterSuccess, newsletterData, isNewsletterError, newsletterError, notify],
  );

  useEffect(
    () => {
      if (isSuccess && data?.length) {
        const selectedCategory = data.find(
          (category: { id: number }) => category.id === categoryId,
        );

        const type = selectedCategory?.fields?.type || null;
        setSelectedCategoryType(type);

        if (type !== CategoryType.ADDITIONAL) {
          setValue(source, null);
        }
      }

      if (isError) {
        showErrorNotification(error, notify);
      }
    },
    [categoryId, data, error, isError, isSuccess, notify, setValue, source],
  );

  if (selectedCategoryType !== CategoryType.ADDITIONAL) {
    return null;
  }

  return (
    <AutocompleteInput
      {...restInputProps}
      className={disabledClassName}
      label={text.newsletterCategory}
      source={source}
      choices={availableCategory}
      validate={selectedCategoryType === CategoryType.ADDITIONAL ? required() : undefined}
    />
  );
};

export {
  NewsletterCategorySelector,
};
